import { useAuth, useClerk, useUser } from '@clerk/react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlinedIcon from '@mui/icons-material/ErrorOutlined';
import {
  Alert,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Typography,
} from '@mui/material';
import { useInjection } from 'inversify-react';
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';
import { RoutesList } from '../../config/routes-list';
import { AdminInvitePublicService } from '../../services/admin-invite/admin-invite-public.service';
import type {
  AdminInviteDetails,
  IAdminInvitePublicService,
} from '../../services/admin-invite/admin-invite-public.service.interface';
import { InviteError } from '../../services/invite/invite.service';
import { userNeedsPassword } from '../../utils/user-needs-password';

type PageState = 'loading' | 'ready' | 'signing-in' | 'success' | 'error';

type Translate = (key: string, options?: Record<string, unknown>) => string;

/** Maps the invite failure to the message pair the operator sees. */
function resolveErrorContent(
  t: Translate,
  errorStatus: number | null,
  errorCode: string | undefined,
): { title: string; detail: string } {
  if (errorStatus === 410 && errorCode === 'ALREADY_USED') {
    return {
      title: t('adminInvite.accept.alreadyUsedTitle'),
      detail: t('adminInvite.accept.alreadyUsedDetail'),
    };
  }
  if (errorStatus === 410) {
    return {
      title: t('adminInvite.accept.expired'),
      detail: t('adminInvite.accept.expiredDetail'),
    };
  }
  if (errorStatus === 404) {
    return {
      title: t('adminInvite.accept.notFound'),
      detail: t('adminInvite.accept.notFoundDetail'),
    };
  }
  return {
    title: t('adminInvite.accept.error'),
    detail: t('adminInvite.accept.errorDetail'),
  };
}

function InviteShell({ children }: { children: ReactNode }) {
  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Card>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>{children}</CardContent>
      </Card>
    </Container>
  );
}

/** Email plus what the invite grants: shown both before and during sign-in. */
function InviteSummary({ details, t }: { details: AdminInviteDetails; t: Translate }) {
  return (
    <Alert severity="info" sx={{ mt: 2, textAlign: 'left' }}>
      <Typography variant="body2">{details.email}</Typography>
      <Typography variant="body2">
        {details.role === 'CONSULTANT'
          ? t('adminInvite.accept.permanentAccess')
          : t('adminInvite.accept.trialDays', { days: details.trialDays })}
      </Typography>
    </Alert>
  );
}

/** "Cosa ottiene": visibile solo per l'invito consulente, sotto il riepilogo email/accesso. */
function ConsultantBenefits({ t }: { t: Translate }) {
  return (
    <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'left' }}>
      {t('adminInvite.accept.consultantBenefitClients')}
    </Typography>
  );
}

/** Titolo specifico per tipo di invito: un titolo generico ("Il tuo invito
 * amministratore") non fa capire a un consulente invitato da Prospero, o a un
 * titolare in prova, cosa sta per attivare. */
function readyTitle(details: AdminInviteDetails, t: Translate): string {
  switch (details.role) {
    case 'CONSULTANT':
      return t('adminInvite.accept.readyTitleConsultant');
    case 'PLATFORM_ADMIN':
      return t('adminInvite.accept.readyTitlePlatformAdmin');
    default:
      return t('adminInvite.accept.readyTitleTenantAdmin');
  }
}

/** Sottotitolo "chi invita e per cosa": solo per il consulente, con il nome
 * dell'azienda consulente-only quando il backend lo espone. */
function readySubtitle(details: AdminInviteDetails, t: Translate): string | null {
  if (details.role !== 'CONSULTANT') {
    return null;
  }
  return details.partnerCompanyName
    ? t('adminInvite.accept.consultantSubtitleWithCompany', {
        companyName: details.partnerCompanyName,
      })
    : t('adminInvite.accept.consultantSubtitle');
}

/** "Cosa succede premendo il pulsante": per il consulente spiega la creazione
 * account passwordless seguita dal passo password; per gli altri resta il
 * testo generico esistente. */
function readyHint(details: AdminInviteDetails, t: Translate): string {
  return details.role === 'CONSULTANT'
    ? t('adminInvite.accept.consultantReadyHint')
    : t('adminInvite.accept.readyHint');
}

function activateLabel(details: AdminInviteDetails, isEmailMatch: boolean, t: Translate): string {
  if (isEmailMatch) {
    return t('adminInvite.accept.continueCta');
  }
  if (details.role === 'CONSULTANT') {
    return t('adminInvite.accept.acceptInviteCta');
  }
  if (details.role === 'PLATFORM_ADMIN') {
    return t('adminInvite.accept.acceptPlatformAdminCta');
  }
  return t('adminInvite.accept.activateCta');
}

export function AdminInviteAcceptPage() {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const frozenToken = useRef(token).current;
  const { isSignedIn } = useAuth();
  const { user } = useUser();
  const clerk = useClerk();
  const navigate = useNavigate();
  const service = useInjection<IAdminInvitePublicService>(AdminInvitePublicService);
  const [pageState, setPageState] = useState<PageState>('loading');
  const [details, setDetails] = useState<AdminInviteDetails | null>(null);
  const [errorStatus, setErrorStatus] = useState<number | null>(null);
  const [errorCode, setErrorCode] = useState<string | undefined>(undefined);
  const [hasAttempted, setHasAttempted] = useState(false);

  const redirectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const failWith = useCallback((err: unknown) => {
    setErrorStatus(err instanceof InviteError ? err.status : 0);
    setErrorCode(err instanceof InviteError ? err.code : undefined);
    setPageState('error');
  }, []);

  const succeedAndRedirect = useCallback(() => {
    setPageState('success');
    redirectTimerRef.current = setTimeout(() => navigate('/', { replace: true }), 800);
  }, [navigate]);

  useEffect(() => {
    return () => {
      if (redirectTimerRef.current) {
        clearTimeout(redirectTimerRef.current);
      }
    };
  }, []);

  // Read-only on mount: no user/session is created until the user clicks "activate".
  const loadDetails = useCallback(
    async (inviteToken: string) => {
      try {
        const fetched = await service.getByToken(inviteToken);
        setDetails(fetched);
        setPageState('ready');
      } catch (err) {
        failWith(err);
      }
    },
    [service, failWith],
  );

  useEffect(() => {
    if (hasAttempted || !frozenToken) {
      return;
    }
    setHasAttempted(true);
    loadDetails(frozenToken);
  }, [hasAttempted, frozenToken, loadDetails]);

  const currentEmail = user?.primaryEmailAddress?.emailAddress ?? '';
  const isEmailMatch =
    !!isSignedIn && !!details && currentEmail.toLowerCase() === details.email.toLowerCase();
  const isEmailMismatch = !!isSignedIn && !!details && !isEmailMatch;

  const handleActivate = useCallback(async () => {
    if (!details || !frozenToken) {
      return;
    }
    if (isEmailMatch) {
      // Already the right account, already signed in: nothing to mint, just go in.
      navigate('/', { replace: true });
      return;
    }
    // Leave 'ready' before any await: the button unmounts with the state
    // change, closing the double-click double-mint window during signOut.
    setPageState('signing-in');
    try {
      if (isEmailMismatch) {
        await clerk.signOut();
      }
      const { ticket } = await service.createMagicLink(frozenToken);
      const signIn = clerk.client.signIn;
      const result = await signIn.create({ strategy: 'ticket', ticket });
      if (result.status !== 'complete' || !result.createdSessionId) {
        throw new Error(`Sign-in incomplete (status: ${result.status})`);
      }
      await clerk.setActive({ session: result.createdSessionId });
      // Utente passwordless (creato senza password lato Clerk, vedi
      // PartnerCompanyClerkService): senza una password non potrà mai più
      // rientrare quando questa sessione scade. Salta lo schermo di
      // "successo" (fuorviante: non è ancora davvero dentro) e manda dritto
      // al passo obbligatorio di impostazione password, riusando la stessa
      // pagina generica dell'invito team.
      const clerkUser = clerk.user;
      if (
        clerkUser &&
        userNeedsPassword({
          passwordEnabled: clerkUser.passwordEnabled,
          externalAccounts: clerkUser.externalAccounts,
        })
      ) {
        navigate(RoutesList.SET_PASSWORD, { replace: true });
        return;
      }
      succeedAndRedirect();
    } catch (err) {
      failWith(err);
    }
  }, [
    details,
    frozenToken,
    isEmailMatch,
    isEmailMismatch,
    service,
    clerk,
    navigate,
    succeedAndRedirect,
    failWith,
  ]);

  const errorContent = resolveErrorContent(t, errorStatus, errorCode);

  if (pageState === 'success') {
    return (
      <InviteShell>
        <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          {t('adminInvite.accept.success')}
        </Typography>
      </InviteShell>
    );
  }

  if (pageState === 'error') {
    return (
      <InviteShell>
        <ErrorOutlinedIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          {errorContent.title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {errorContent.detail}
        </Typography>
        <Button component={Link} to="/sign-in" variant="contained" sx={{ mt: 2, minHeight: 48 }}>
          {t('adminInvite.accept.goToLogin')}
        </Button>
      </InviteShell>
    );
  }

  if (pageState === 'ready' && details) {
    const subtitle = readySubtitle(details, t);
    return (
      <InviteShell>
        <Typography variant="h6" gutterBottom>
          {readyTitle(details, t)}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        )}
        <InviteSummary details={details} t={t} />
        {details.role === 'CONSULTANT' && <ConsultantBenefits t={t} />}
        {isEmailMismatch && (
          <Alert severity="warning" sx={{ mt: 2, textAlign: 'left' }}>
            {t('adminInvite.accept.sessionMismatchWarning', {
              current: currentEmail,
              invited: details.email,
            })}
          </Alert>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          {readyHint(details, t)}
        </Typography>
        <Button
          onClick={handleActivate}
          variant="contained"
          fullWidth
          sx={{ mt: 3, minHeight: 48 }}
        >
          {activateLabel(details, isEmailMatch, t)}
        </Button>
      </InviteShell>
    );
  }

  return (
    <InviteShell>
      <CircularProgress sx={{ mb: 2 }} />
      <Typography variant="h6" gutterBottom>
        {pageState === 'signing-in'
          ? t('adminInvite.accept.signingIn')
          : t('adminInvite.accept.loading')}
      </Typography>
      {details && pageState === 'signing-in' && <InviteSummary details={details} t={t} />}
    </InviteShell>
  );
}
